import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiLoader } from '@taiga-ui/core';

type ProxyPageConfig = {
	defaultTitleKey?: string;
	defaultDescriptionKey?: string;
	redirectUrl?: string;
	redirectTimeout?: number;
};

@Component({
	standalone: true,
	selector: 'ctx-proxy-page',
	imports: [TuiLoader, TranslocoModule],
	template: `
		<div class="flex flex-col items-center justify-center gap-4">
			<div class="text-center">
				<h1 class="text-2xl font-bold">
					{{ titleKey() | transloco }}
				</h1>
				<p>{{ descriptionKey() | transloco }}</p>
			</div>
			<tui-loader class="size-16" size="xl" />
		</div>
	`,
	host: { class: 'flex h-screen w-screen items-center justify-center' },
})
export class ProxyPageComponent {
	private readonly route = inject(ActivatedRoute);

	private readonly fallbackTitleKey = 'external.landing.title';
	private readonly fallbackDescriptionKey = 'external.landing.description';

	readonly titleKey = signal<string | null>(null);
	readonly descriptionKey = signal<string | null>(null);
	readonly redirectUrl = signal<string | null>(null);
	readonly redirectTimeout = signal<number>(3000);

	ngOnInit() {
		const config = this.route.snapshot.data['proxyPage'] as
			ProxyPageConfig | undefined;
		const queryParams = this.route.snapshot.queryParamMap;

		const defaultTitleKey =
			config?.defaultTitleKey ?? this.fallbackTitleKey;
		const defaultDescriptionKey =
			config?.defaultDescriptionKey ?? this.fallbackDescriptionKey;

		this.titleKey.set(queryParams.get('title') ?? defaultTitleKey);
		this.descriptionKey.set(
			queryParams.get('description') ?? defaultDescriptionKey,
		);
		this.redirectUrl.set(config?.redirectUrl ?? null);
		if (config?.redirectTimeout) {
			this.redirectTimeout.set(config.redirectTimeout);
		}
	}

	ngAfterViewInit() {
		// Timeout to give the user time to read the message.
		setTimeout(() => {
			this.redirectToDestination();
		}, this.redirectTimeout());
	}

	private redirectToDestination() {
		const url = this.redirectUrl();
		if (!url) {
			console.warn('No redirect URL found');
			return;
		}
		window.location.replace(url);
	}
}
